/**
 * 公共业务工具方法
 * 存放多模块复用的、与业务场景相关但无 Vue 依赖的纯 JS helper
 */

/**
 * 补零工具（内部使用，不导出）
 * @param {Number|String} n 数字
 * @returns {String} 两位字符串
 */
const _pad = n => String(n).padStart(2, '0');

/**
 * 获取当前时间的中文年月日时分秒格式
 * 示例：2026年08月24日15时30分22秒
 * @param {Date|Number|String} [time] 可选，传入指定时间，不传则取当前时间
 * @returns {String} 格式化后的时间字符串
 */
export function formatCnDateTime(time) {
  const d = time ? new Date(time) : new Date();
  if (Number.isNaN(d.getTime())) {
    const now = new Date();
    return `${now.getFullYear()}年${_pad(now.getMonth() + 1)}月${_pad(now.getDate())}日${_pad(now.getHours())}时${_pad(now.getMinutes())}分${_pad(now.getSeconds())}秒`;
  }
  return `${d.getFullYear()}年${_pad(d.getMonth() + 1)}月${_pad(d.getDate())}日${_pad(d.getHours())}时${_pad(d.getMinutes())}分${_pad(d.getSeconds())}秒`;
}

/**
 * 从下载 URL 中提取文件后缀名
 * 优先走 URL API 解析 pathname，失败则回退字符串截取与正则匹配
 * @param {String} url 文件下载链接
 * @param {String} [defaultSuffix='xlsx'] 无法识别时的兜底后缀
 * @returns {String} 小写后缀名（不含点号），例如 'xlsx'
 */
export function extractFileSuffix(url, defaultSuffix = 'xlsx') {
  if (!url || typeof url !== 'string') return defaultSuffix;
  let suffix = defaultSuffix;
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const dotIdx = pathname.lastIndexOf('.');
    if (dotIdx > -1) {
      const result = pathname.slice(dotIdx + 1).toLowerCase();
      if (result) suffix = result;
    }
  } catch (e) {
    const dotIdx = url.lastIndexOf('.');
    if (dotIdx > -1) {
      const suffixStr = url.slice(dotIdx + 1).toLowerCase();
      const suffixMatch = suffixStr.match(/^[a-zA-Z0-9]+/);
      if (suffixMatch) suffix = suffixMatch[0];
    }
  }
  return suffix;
}

/**
 * 构造下载文件的文件名
 * 规则：当前点击时间(中文年月日时分秒)_业务标识1_业务标识2.后缀名
 * 示例：2026年08月24日15时30分22秒_某某公司_发票信息.xlsx
 *
 * @param {Object} options 配置项
 * @param {String} options.url                下载链接（用于提取后缀）
 * @param {Array<String>} [options.labels=[]] 业务标识数组，按顺序以下划线拼接
 * @param {String} [options.suffix]           手动指定后缀（含不含点号均可），未传时从 url 自动提取
 * @param {String} [options.defaultSuffix='xlsx'] 后缀提取失败时的兜底
 * @param {Date|Number|String} [options.time] 指定时间戳/时间对象，不传则取当前时间
 * @returns {String} 组装完成的带后缀文件名
 */
export function buildDownloadFileName(options = {}) {
  const {
    url = '',
    labels = [],
    suffix,
    defaultSuffix = 'xlsx',
    time,
  } = options;

  const timeStr = formatCnDateTime(time);

  const safeLabels = (Array.isArray(labels) ? labels : [labels])
    .map(item => (item == null ? '' : String(item).trim()))
    .filter(Boolean);
  const labelStr = safeLabels.length > 0 ? safeLabels.join('_') : '下载文件';

  let fileSuffix;
  if (suffix && typeof suffix === 'string' && suffix.trim()) {
    fileSuffix = suffix.trim().replace(/^\./, '').toLowerCase();
  } else {
    fileSuffix = extractFileSuffix(url, defaultSuffix);
  }

  return `${timeStr}_${labelStr}.${fileSuffix}`;
}

/**
 * 公共下载地址链接方法
 *
 * 说明：项目中下载链接不需要后端接口 header 鉴权，只要能下就行；
 * 因此直接使用 <a href download> 导航级请求触发下载，
 * 不做 axios/XHR（会触发 CORS），也不分同源/跨域，逻辑最简。
 *
 * 注：跨域资源浏览器会忽略 <a download> 属性，文件名以服务器响应为准；
 * 此处仍拼 attname 参数并设置 a.download，尽量争取。
 *
 * @param {String} url           下载链接（必填）
 * @param {String} [fileName=''] 自定义文件名（含后缀）。用于拼接 attname 参数与 a.download
 * @param {Object} [options={}]  额外配置
 * @param {String} [options.attnameKey='attname']   拼接在 URL 上的"建议文件名"参数名
 * @returns {void}
 */
export function publicDownloadUrlLink(url, fileName = '', options = {}) {
  if (!url || typeof url !== 'string') {
    console.warn('[publicDownloadUrlLink] 下载链接为空或类型不正确，url =', url);
    return;
  }

  const safeFileName = fileName == null ? '' : String(fileName);
  const { attnameKey = 'attname' } = options;

  // 拼接 attname 参数，让服务端有机会识别"建议文件名"
  let finalUrl = url;
  if (attnameKey && safeFileName) {
    const sep = finalUrl.indexOf('?') > -1 ? '&' : '?';
    finalUrl = `${finalUrl}${sep}${encodeURIComponent(attnameKey)}=${encodeURIComponent(safeFileName)}`;
  }

  const a = document.createElement('a');
  a.href = finalUrl;
  if (safeFileName) a.download = decodeURI(safeFileName);
  a.rel = 'noopener';
  a.target = '_self';
  a.style.display = 'none';
  // Firefox 等部分浏览器需要插入 DOM 才能正常触发 click
  document.body.appendChild(a);
  a.click();
  // 延迟移除，确保 click 事件派发完成
  setTimeout(() => {
    if (a.parentNode) a.parentNode.removeChild(a);
  }, 0);
}
